import React from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  useTheme,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import SchoolIcon from '@mui/icons-material/School';
import BusinessIcon from '@mui/icons-material/Business';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DescriptionIcon from '@mui/icons-material/Description';
import VerifiedIcon from '@mui/icons-material/Verified';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RoomServiceIcon from '@mui/icons-material/RoomService';

const Services = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // 文档类型配置 - 与 /workspace/create 页面的 DOCUMENT_TYPES 保持一致
  const primaryColor = '#4353FF';
  const documentTypes = [
    {
      id: 'personal',
      name: '个人证件',
      description: '身份证、驾驶证等个人证件翻译认证',
      icon: <PersonIcon sx={{ fontSize: 48, color: primaryColor }} />,
      color: primaryColor,
      docs: ['身份证', '出生证明', '工作证', '驾驶证', '行驶证', '车辆登记证书', '退休证']
    },
    {
      id: 'family',
      name: '家庭证件',
      description: '结婚证、离婚证、户口本等家庭证件翻译',
      icon: <FamilyRestroomIcon sx={{ fontSize: 48, color: primaryColor }} />,
      color: primaryColor,
      docs: ['结婚证', '离婚证', '户口本', '房产证']
    },
    {
      id: 'education',
      name: '学历证书',
      description: '毕业证、学位证等学历相关翻译',
      icon: <SchoolIcon sx={{ fontSize: 48, color: primaryColor }} />,
      color: primaryColor,
      docs: [
        '毕业证',
        '学位证',
        '学生证',
        '教师资格证'
      ]
    },
    {
      id: 'business',
      name: '企业证件',
      description: '营业执照、商标证书等企业文件翻译',
      icon: <BusinessIcon sx={{ fontSize: 48, color: primaryColor }} />,
      color: primaryColor,
      docs: ['营业执照', '税务登记证', '商标证书', '开户许可证']
    },
    {
      id: 'financial',
      name: '流水清单',
      description: '银行流水账单、纳税清单等财务文件翻译',
      icon: <ReceiptLongIcon sx={{ fontSize: 48, color: primaryColor }} />,
      color: primaryColor,
      docs: ['银行流水账单', '个人所得税纳税清单']
    },
    {
      id: 'other',
      name: '其它证明',
      description: '在职证明、离职证明等其他证明文件翻译',
      icon: <DescriptionIcon sx={{ fontSize: 48, color: primaryColor }} />,
      color: primaryColor,
      docs: ['在读证明', '实习证明', '在职证明', '工作证明', '离职证明']
    }
  ];

  const advantages = [
    '专业译员团队，具备相关领域背景',
    '严格质量控制，多重校对审核',
    '快速交付，支持加急服务',
    '格式保持，确保文档美观',
    '保密协议，信息安全有保障',
    '售后支持，一对一服务修改'
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* 页面标题 */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <RoomServiceIcon sx={{ fontSize: 60, color: theme.palette.primary.main, mb: 2 }} />
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(90deg, #4353FF 0%, #6B78FF 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            mb: 2
          }}
        >
          专业服务范围
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}
        >
          翻译范围
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
          {documentTypes.map((docType) => (
            <Chip
              key={docType.id}
              label={docType.name}
              sx={{
                backgroundColor: docType.color + '15',
                color: docType.color,
                fontWeight: 600
              }}
            />
          ))}
        </Box>
      </Box>

      {/* 文档类型卡片矩阵 */}
      <Grid container spacing={4} sx={{ mb: 8 }}>
        {documentTypes.map((docType) => (
          <Grid item xs={12} sm={6} md={4} key={docType.id}>
            <Card
              sx={{
                height: '100%',
                p: 2,
                borderRadius: 3,
                transition: 'all 0.3s ease',
                border: `2px solid transparent`,
                position: 'relative',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: `0 12px 35px ${docType.color}20`,
                  border: `2px solid ${docType.color}30`
                }
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2.4, textAlign: 'center' }}>
                  {docType.icon}
                  <Typography variant="h5" sx={{ fontWeight: 600, mt: 1.6, mb: 0.8, color: docType.color }}>
                    {docType.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    {docType.description}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                  {docType.docs.map((doc, idx) => {
                    const docName = typeof doc === 'string' ? doc : doc.name;
                    const isDisabled = typeof doc === 'object' && doc.disabled;
                    return (
                      <Chip
                        key={idx}
                        label={isDisabled ? `${docName}（维护中）` : docName}
                        size="small"
                        variant="outlined"
                        title={isDisabled ? doc.reason : undefined}
                        sx={{
                          borderColor: theme.palette.grey[300],
                          color: isDisabled ? '#9e9e9e' : theme.palette.text.secondary,
                          fontSize: '0.75rem',
                          textDecoration: isDisabled ? 'line-through' : 'none',
                          '&:hover': {
                            backgroundColor: docType.color + '10',
                            borderColor: docType.color + '50'
                          }
                        }}
                      />
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* 服务优势 */}
      <Box sx={{ mb: 8, mt: 24 }}>
        <Typography
          variant="h3"
          sx={{
            textAlign: 'center',
            mb: 8,
            fontWeight: 600,
            color: theme.palette.primary.main
          }}
        >
          我们的服务优势
        </Typography>
        <Card sx={{ p: 4, borderRadius: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                专业服务保障
              </Typography>
              <List>
                {advantages.slice(0, 3).map((advantage, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon
                        sx={{
                          fontSize: 20,
                          color: theme.palette.success.main
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={advantage}
                      primaryTypographyProps={{
                        variant: 'body2'
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                质量与服务
              </Typography>
              <List>
                {advantages.slice(3).map((advantage, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon
                        sx={{
                          fontSize: 20,
                          color: theme.palette.success.main
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={advantage}
                      primaryTypographyProps={{
                        variant: 'body2'
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </Card>
      </Box>

      {/* 质量保证 */}
      <Card
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          p: 4,
          borderRadius: 3,
          textAlign: 'center',
          color: theme.palette.primary.main,
          mb: 8,
          mt: 24
        }}
      >
        <VerifiedIcon sx={{ fontSize: 64, color: theme.palette.primary.main, mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          专业品质保证
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
          我们的译员团队具备相关专业背景，熟悉各国签证、移民、留学等申请要求，
          确保翻译文件符合官方标准，助您顺利完成各项申请。
        </Typography>
        {/* <Button
          variant="contained"
          size="large"
          onClick={() => {
            navigate('/workspace');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          sx={{
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            borderRadius: 3,
            boxShadow: '0 8px 25px rgba(67, 83, 255, 0.3)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 35px rgba(67, 83, 255, 0.4)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          立即开始翻译
        </Button> */}
      </Card>
    </Container>
  );
};

export default Services;
